import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Template } from '../types';
import { templateService } from '../services/api';
import './TemplateDetails.css';

const TemplateDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        if (!id) {
          throw new Error('ID du modèle non spécifié');
        }
        
        const data = await templateService.getById(parseInt(id));
        setTemplate(data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération du modèle:', err);
        setError('Erreur lors de la récupération du modèle. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTemplate();
  }, [id]);
  
  const handleDelete = async () => {
    if (!template) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le modèle "${template.name}" ?`)) {
      try {
        setLoading(true);
        await templateService.delete(template.id);
        navigate('/templates');
      } catch (err) {
        console.error('Erreur lors de la suppression du modèle:', err);
        setError('Erreur lors de la suppression du modèle. Veuillez réessayer.');
        setLoading(false);
      }
    }
  };
  
  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'confirmation':
        return 'Confirmation';
      case 'reminder':
        return 'Rappel';
      case 'cancellation':
        return 'Annulation';
      case 'other':
        return 'Autre';
      default:
        return category;
    }
  };
  
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  if (loading) {
    return <div className="loading">Chargement du modèle...</div>;
  }
  
  if (error) {
    return (
      <div className="template-details-container">
        <div className="error-message">{error}</div>
        <button 
          className="btn-secondary"
          onClick={() => navigate('/templates')}
        >
          Retour à la liste
        </button>
      </div>
    );
  }
  
  if (!template) {
    return (
      <div className="template-details-container">
        <div className="error-message">Modèle non trouvé.</div>
        <button 
          className="btn-secondary"
          onClick={() => navigate('/templates')}
        >
          Retour à la liste
        </button>
      </div>
    );
  }
  
  return (
    <div className="template-details-container">
      <div className="template-details-header">
        <h1>{template.name}</h1>
        <div className="template-actions">
          <Link to="/templates" className="btn-secondary">
            Retour à la liste
          </Link>
          <Link to={`/templates/${template.id}/edit`} className="btn-primary">
            Modifier
          </Link>
          <button 
            className="btn-danger"
            onClick={handleDelete}
          >
            Supprimer
          </button>
        </div>
      </div>
      
      <div className="template-details-content">
        <div className="template-info">
          <div className="info-group">
            <span className="info-label">Type:</span>
            <span className={`badge badge-${template.type}`}>
              {template.type === 'email' ? 'Email' : 'SMS'}
            </span>
          </div>
          
          <div className="info-group">
            <span className="info-label">Catégorie:</span>
            <span className="badge badge-category">
              {getCategoryLabel(template.category)}
            </span>
          </div>
          
          <div className="info-group">
            <span className="info-label">Créé le:</span>
            <span>{formatDate(template.created_at)}</span>
          </div>
          
          <div className="info-group">
            <span className="info-label">Dernière modification:</span>
            <span>{formatDate(template.updated_at)}</span>
          </div>
        </div>
        
        <div className="template-content-section">
          {template.type === 'email' && template.subject && (
            <div className="template-subject">
              <h3>Sujet</h3>
              <div className="subject-content">{template.subject}</div>
            </div>
          )}
          
          <div className="template-content">
            <h3>Contenu</h3>
            <div className="content-container">
              {template.type === 'email' ? (
                <div className="email-content">
                  <pre>{template.content}</pre>
                  <div className="content-divider"></div>
                  <h4>Aperçu du rendu HTML:</h4>
                  <div 
                    className="html-preview"
                    dangerouslySetInnerHTML={{ __html: template.content }}
                  />
                </div>
              ) : (
                <div className="sms-content">
                  <pre>{template.content}</pre>
                </div>
              )}
            </div>
          </div>
          
          <div className="template-placeholders">
            <h3>Balises dynamiques disponibles</h3>
            <ul className="placeholders-list">
              <li><code>{"{{civilite}}"}</code> - Civilité du patient (Monsieur/Madame)</li>
              <li><code>{"{{nom_patient}}"}</code> - Prénom du patient</li>
              <li><code>{"{{nom_complet}}"}</code> - Prénom et nom du patient</li>
              <li><code>{"{{date_rdv}}"}</code> - Date du rendez-vous (format long)</li>
              <li><code>{"{{heure_rdv}}"}</code> - Heure du rendez-vous</li>
              <li><code>{"{{type}}"}</code> - Type de rendez-vous</li>
              <li><code>{"{{date_courte}}"}</code> - Date du rendez-vous (format court)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateDetails;
